import type { IToken } from 'chevrotain';
import { v4 as uuid } from 'uuid';

import {
  GEOSPARQL_JSON_LITERAL_IRI,
  GEOSPARQL_WKT_LITERAL_IRI,
  RDF_TYPE_IRI,
  STARDOG_LABEL_IRI,
  STARDOG_PROPERTY_TEXT_MATCH_IRI,
  XSD_BOOLEAN_IRI,
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_DAY_TIME_DURATION_IRI,
  XSD_DECIMAL_IRI,
  XSD_DOUBLE_IRI,
  XSD_DURATION_IRI,
  XSD_FLOAT_IRI,
  XSD_INTEGER_IRI,
  XSD_STRING_IRI,
  XSD_TIME_IRI,
  XSD_YEAR_MONTH_DURATION_IRI,
} from 'bones/constants/iris';
import {
  ANY_CLASS_IRI,
  DATE_SPLIT_SYMBOL,
  DEFAULT_OPERATOR_TYPE_BY_DATATYPE_RANGE,
  DatatypeOperatorType,
  DatatypeOperatorTypeValue,
  DatatypeRange,
  Direction,
  INSTANCE_IRI,
  InstanceOperatorType,
  ObjectOperatorType,
  OperatorType,
  XSD_DATATYPE_IRIS,
} from 'bones/utils/queryBuilder/constants';
import { createNewTopExpression } from 'bones/utils/queryBuilder/helpers/create';
import { removeDraftFields } from 'bones/utils/queryBuilder/helpers/draft';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';
import { getFullIri } from 'bones/utils/queryBuilder/parsing/helpers/getFullIri';
import { getTokenName } from 'bones/utils/queryBuilder/parsing/helpers/getTokenName';
import { sparqlTokens } from 'bones/utils/queryBuilder/parsing/helpers/sparql';
import {
  SparqlFilter,
  SparqlFilterResult,
  SparqlLiteralToken,
  SparqlResult,
  SparqlResultType,
  SparqlToken,
  SparqlTokenType,
  SparqlTripleResult,
  SparqlVarToken,
  sparqlTriples,
} from 'bones/utils/queryBuilder/parsing/sparqlTriples';
import {
  Filter,
  TopExpression,
  Variable,
} from 'bones/utils/queryBuilder/types';

export const getSparqlTokenImage = (token: SparqlToken): IToken => {
  switch (token.type) {
    case SparqlTokenType.IRI:
      return token.iri;
    case SparqlTokenType.LITERAL:
      return token.literal;
    default:
      // SparqlTokenType.VAR
      return token.var;
  }
};

const tokenToDatatypeOperatorType = (
  range: string | null,
  operatorToken: IToken,
  reverse: boolean
): DatatypeOperatorTypeValue | null => {
  const operatorTokenName = getTokenName(operatorToken);
  switch (range) {
    case XSD_BOOLEAN_IRI:
      switch (operatorTokenName) {
        case sparqlTokens.sparqlTokenMap.Equals.name: {
          return DatatypeOperatorType.IS;
        }
        default:
          return null;
      }
    case XSD_DATE_IRI:
    case XSD_DATE_TIME_IRI:
    case XSD_TIME_IRI:
      switch (operatorTokenName) {
        case sparqlTokens.sparqlTokenMap.Equals.name: {
          return DatatypeOperatorType.ON;
        }
        case sparqlTokens.sparqlTokenMap.NotEquals.name: {
          return DatatypeOperatorType.NOT_ON;
        }
        case sparqlTokens.sparqlTokenMap.LessThan.name: {
          return !reverse
            ? DatatypeOperatorType.BEFORE
            : DatatypeOperatorType.AFTER;
        }
        case sparqlTokens.sparqlTokenMap.GreaterThan.name: {
          return !reverse
            ? DatatypeOperatorType.AFTER
            : DatatypeOperatorType.BEFORE;
        }
        case sparqlTokens.sparqlTokenMap.LessThanEquals.name: {
          return !reverse
            ? DatatypeOperatorType.BEFORE_OR_ON
            : DatatypeOperatorType.AFTER_OR_ON;
        }
        case sparqlTokens.sparqlTokenMap.GreaterThanEquals.name: {
          return !reverse
            ? DatatypeOperatorType.AFTER_OR_ON
            : DatatypeOperatorType.BEFORE_OR_ON;
        }
        // istanbul ignore next
        default:
          return null;
      }
    case XSD_DAY_TIME_DURATION_IRI:
    case XSD_DECIMAL_IRI:
    case XSD_DOUBLE_IRI:
    case XSD_DURATION_IRI:
    case XSD_FLOAT_IRI:
    case XSD_INTEGER_IRI:
    case XSD_YEAR_MONTH_DURATION_IRI:
      switch (operatorTokenName) {
        case sparqlTokens.sparqlTokenMap.Equals.name: {
          return DatatypeOperatorType.EQUALS;
        }
        case sparqlTokens.sparqlTokenMap.NotEquals.name: {
          return DatatypeOperatorType.DOES_NOT_EQUAL;
        }
        case sparqlTokens.sparqlTokenMap.LessThan.name: {
          return !reverse ? DatatypeOperatorType.LT : DatatypeOperatorType.GT;
        }
        case sparqlTokens.sparqlTokenMap.GreaterThan.name: {
          return !reverse ? DatatypeOperatorType.GT : DatatypeOperatorType.LT;
        }
        case sparqlTokens.sparqlTokenMap.LessThanEquals.name: {
          return !reverse ? DatatypeOperatorType.LTE : DatatypeOperatorType.GTE;
        }
        case sparqlTokens.sparqlTokenMap.GreaterThanEquals.name: {
          return !reverse ? DatatypeOperatorType.GTE : DatatypeOperatorType.LTE;
        }
        // istanbul ignore next
        default:
          return null;
      }
    case null:
    case GEOSPARQL_JSON_LITERAL_IRI:
    case GEOSPARQL_WKT_LITERAL_IRI:
    case XSD_STRING_IRI:
    default:
      switch (operatorTokenName) {
        case sparqlTokens.sparqlTokenMap.Equals.name: {
          return DatatypeOperatorType.EQUALS;
        }
        case sparqlTokens.sparqlTokenMap.NotEquals.name: {
          return DatatypeOperatorType.DOES_NOT_EQUAL;
        }
        default:
          return null;
      }
  }
};

const BETWEEN_RANGES = new Set<string | null>([
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_TIME_IRI,
]);

export const TERMINAL_TOKEN_NAMES = new Set<string | undefined>([
  sparqlTokens.sparqlTokenMap.IRIREF.name,
  sparqlTokens.sparqlTokenMap.PNAME_NS.name,
  sparqlTokens.sparqlTokenMap.PNAME_LN.name,
  sparqlTokens.sparqlTokenMap.BLANK_NODE_LABEL.name,
  sparqlTokens.sparqlTokenMap.VAR1.name,
  sparqlTokens.sparqlTokenMap.VAR2.name,
  sparqlTokens.sparqlTokenMap.LANGTAG.name,
  sparqlTokens.sparqlTokenMap.INTEGER.name,
  sparqlTokens.sparqlTokenMap.DECIMAL.name,
  sparqlTokens.sparqlTokenMap.DOUBLE.name,
  sparqlTokens.sparqlTokenMap.INTEGER_POSITIVE.name,
  sparqlTokens.sparqlTokenMap.DECIMAL_POSITIVE.name,
  sparqlTokens.sparqlTokenMap.DOUBLE_POSITIVE.name,
  sparqlTokens.sparqlTokenMap.INTEGER_NEGATIVE.name,
  sparqlTokens.sparqlTokenMap.DECIMAL_NEGATIVE.name,
  sparqlTokens.sparqlTokenMap.DOUBLE_NEGATIVE.name,
  sparqlTokens.sparqlTokenMap.STRING_LITERAL1.name,
  sparqlTokens.sparqlTokenMap.STRING_LITERAL2.name,
  sparqlTokens.sparqlTokenMap.STRING_LITERAL_LONG1.name,
  sparqlTokens.sparqlTokenMap.STRING_LITERAL_LONG2.name,
  sparqlTokens.sparqlTokenMap.NIL.name,
  sparqlTokens.sparqlTokenMap.ANON.name,
  sparqlTokens.sparqlTokenMap.PERCENT.name,
]);

const formatLiteral = (token: IToken) => {
  switch (getTokenName(token)) {
    case sparqlTokens.sparqlTokenMap.STRING_LITERAL1.name:
    case sparqlTokens.sparqlTokenMap.STRING_LITERAL2.name:
      return token.image.slice(1, token.image.length - 1);
    case sparqlTokens.sparqlTokenMap.STRING_LITERAL_LONG1.name:
    case sparqlTokens.sparqlTokenMap.STRING_LITERAL_LONG2.name:
      return token.image.slice(3, token.image.length - 3);
    // istanbul ignore next
    default:
      return '';
  }
};

export const sparqlResults = (
  results: {
    namespaces: Record<string, string>;
    tokens: {
      all: IToken[];
      ignored: IToken[];
      unprocessed: IToken[];
    };
    triples: SparqlResult[];
  },
  { propertiesByIri }: Pick<QueryBuilderModel, 'propertiesByIri'>,
  namespaces: Record<string, string>,
  options?: {
    isRuleIf?: boolean;
    isRuleThen?: boolean;
    stardogLabelIri?: string;
    variablesByImage?: Record<string, Variable>;
  }
): {
  score: number;
  tokens: { all: IToken[]; ignored: IToken[]; unprocessed: IToken[] };
  topExpression: TopExpression;
  variablesByImage: Record<string, Variable>;
} => {
  const unprocessedTokensSet = new Set(results.tokens.unprocessed);
  const addUnprocessedToken = (token: IToken | null) => {
    if (token) {
      unprocessedTokensSet.add(token);
    }
  };

  const allNamespaces = { ...namespaces, ...results.namespaces };

  // collect variable images used in the subject and object position
  const subjectImages = new Set<string>();
  const objectImages = new Set<string>();

  // collect iri tokens used in the subject and object position
  const iriTokens: Record<string, IToken> = {};

  // collect type assertiosn for each variable image
  const typesByImage: Record<string, Set<string> | undefined> = {};
  const addImageType = (triple: SparqlTripleResult) => {
    const subjectToken = getSparqlTokenImage(triple.subject);
    const objectToken = getSparqlTokenImage(triple.object);

    const type = getFullIri(objectToken, allNamespaces);
    if (!type) {
      return;
    }

    const types = typesByImage[subjectToken.image];
    if (!types) {
      typesByImage[subjectToken.image] = new Set([type]);
    } else {
      types.add(type);
    }
  };

  // collect text match images for processing later
  const textMatchByImage: Record<string, SparqlToken> = {};

  // collect comparison filters to process later
  const comparisonFilterResults = new Set<SparqlFilterResult>();

  // collect literal filters by the object variable image to process later
  const literalFilterResultsByImage: Record<
    string,
    Set<SparqlFilterResult>
  > = {};
  const addLiteralFilterResult = (
    token: IToken,
    filter: SparqlFilterResult
  ) => {
    if (!literalFilterResultsByImage[token.image]) {
      literalFilterResultsByImage[token.image] = new Set();
    }
    literalFilterResultsByImage[token.image].add(filter);
  };

  const tripleResults: SparqlTripleResult[] = [];
  results.triples.forEach((result) => {
    if (result.type === SparqlResultType.TRIPLE) {
      const subjectToken = getSparqlTokenImage(result.subject);
      subjectImages.add(subjectToken.image);

      if (result.subject.type === SparqlTokenType.IRI) {
        // add the subject iri token for adding an "in" filter later
        iriTokens[result.subject.iri.image] = result.subject.iri;
      }

      if (result.object.type === SparqlTokenType.VAR) {
        objectImages.add(result.object.var.image);
      }

      if (
        getFullIri(result.predicate, namespaces) === RDF_TYPE_IRI ||
        getTokenName(result.predicate) === sparqlTokens.sparqlTokenMap.A.name
      ) {
        addImageType(result);

        if (!options?.isRuleThen) {
          return;
        }

        // for "classify as" type rules, also register and process the object as a type
        addImageType({ ...result, subject: result.object });
      } else if (result.object.type === SparqlTokenType.IRI) {
        // add the object iri token for adding an "in" filter later
        iriTokens[result.object.iri.image] = result.object.iri;
      }

      // process text match triples separately
      const predicateIri = getFullIri(result.predicate, allNamespaces);
      if (predicateIri === STARDOG_PROPERTY_TEXT_MATCH_IRI) {
        textMatchByImage[subjectToken.image] = result.object;
        return;
      }

      tripleResults.push(result);
    } else {
      result.filters.forEach(({ subject, value }) => {
        if (
          value.type === SparqlTokenType.VAR &&
          subject.type === SparqlTokenType.VAR
        ) {
          comparisonFilterResults.add(result);
        } else if (value.type === SparqlTokenType.VAR) {
          addLiteralFilterResult(value.var, result);
        } else if (subject.type === SparqlTokenType.VAR) {
          addLiteralFilterResult(subject.var, result);
        }
      });
    }
  });

  // extend existing query builder variables
  const variablesByImage: Record<string, Variable> = {
    ...options?.variablesByImage,
  };
  const getOrAssignVariable = (token: IToken): Variable => {
    if (variablesByImage[token.image]) {
      return variablesByImage[token.image];
    }

    const types = typesByImage[token.image];
    const iri = types?.size === 1 ? Array.from(types)[0] : ANY_CLASS_IRI;

    const variable = { id: uuid(), iri };
    variablesByImage[token.image] = variable;

    return variable;
  };

  // collect output attribute filters by variable image
  const attributeFiltersByImage: Record<
    string,
    Array<{ filter: Filter; filterResult?: SparqlFilterResult }>
  > = {};
  const addAttributeFilter = (
    token: IToken,
    filter: Filter,
    filterResult?: SparqlFilterResult
  ) => {
    if (!attributeFiltersByImage[token.image]) {
      attributeFiltersByImage[token.image] = [];
    }

    attributeFiltersByImage[token.image].push({ filter, filterResult });
  };

  const canAssumeIriToken = (token: IToken) =>
    // image is explicitly typed as a concept
    typesByImage[token.image] ||
    // assume that an image is used as a subject elsewhere is a concept (excluding text match)
    (subjectImages.has(token.image) && !textMatchByImage[token.image]);

  const isInequalityFilter = (filter: SparqlFilter) =>
    filter.subject.type === SparqlTokenType.VAR &&
    filter.value.type === SparqlTokenType.VAR &&
    getTokenName(filter.operator) ===
      sparqlTokens.sparqlTokenMap.NotEquals.name &&
    canAssumeIriToken(filter.subject.var) &&
    canAssumeIriToken(filter.value.var);

  const processedLiteralFilters = new Set<string>();
  const unprocessedLiteralFilters = new Set(
    Object.keys(literalFilterResultsByImage)
  );
  const processLiteralFilters = (
    filterResults: Set<SparqlFilterResult> | undefined,
    subject: Variable,
    objectToken: IToken,
    propertyIri: string,
    range: DatatypeRange
  ) => {
    const skippedFilters = Array.from(filterResults || []).filter(
      (filterResult) => {
        if (filterResult.filters.length === 1) {
          const [filter] = filterResult.filters;

          let isReverse = false;
          let filterVar: IToken | undefined;
          let filterLiteral: SparqlLiteralToken | undefined;
          if (
            filter.subject.type === SparqlTokenType.VAR &&
            filter.value.type === SparqlTokenType.LITERAL
          ) {
            filterVar = filter.subject.var;
            if (objectImages.has(filter.subject.var.image)) {
              filterLiteral = filter.value;
            }
          } else if (
            filter.value.type === SparqlTokenType.VAR &&
            filter.subject.type === SparqlTokenType.LITERAL
          ) {
            filterVar = filter.value.var;
            if (objectImages.has(filter.value.var.image)) {
              filterLiteral = filter.subject;
            }

            isReverse = true;
          }

          if (filterVar && filterLiteral) {
            const inferredRange: DatatypeRange =
              range ||
              (filterLiteral.langIri &&
                (getFullIri(filterLiteral.langIri, allNamespaces) as any)) ||
              null;

            if (
              getTokenName(filter.operator) ===
              sparqlTokens.sparqlTokenMap.CONTAINS.name
            ) {
              addAttributeFilter(
                filterVar,
                {
                  subject,
                  operator: {
                    iri: propertyIri,
                    operatorType: OperatorType.DATATYPE,
                    range: inferredRange,
                    type: filter.operatorBang
                      ? DatatypeOperatorType.DOES_NOT_CONTAIN
                      : DatatypeOperatorType.CONTAINS,
                  },
                  value: formatLiteral(filterLiteral.literal),
                },
                filterResult
              );
              return false;
            }

            const datatypeOperatorType = tokenToDatatypeOperatorType(
              inferredRange,
              filter.operator,
              isReverse
            );
            if (datatypeOperatorType) {
              addAttributeFilter(
                filterVar,
                {
                  subject,
                  operator: {
                    iri: propertyIri,
                    operatorType: OperatorType.DATATYPE,
                    range: inferredRange,
                    type: datatypeOperatorType,
                  },
                  value: formatLiteral(filterLiteral.literal),
                },
                filterResult
              );
              return false;
            }
          }
        } else if (filterResult.filters.length === 2) {
          const [firstFilter, secondFilter] = filterResult.filters;

          const firstOperatorTokenName = getTokenName(firstFilter.operator);
          const secondOperatorTokenName = getTokenName(secondFilter.operator);

          let isFirstOperatorGte = false;
          let isFirstOperatorValid = false;
          let firstFilterVar: SparqlVarToken | undefined;
          let secondFilterVar: SparqlVarToken | undefined;
          let isSecondOperatorLte = false;
          let isSecondOperatorValid = false;
          let firstFilterLiteral: SparqlLiteralToken | undefined;
          let secondFilterLiteral: SparqlLiteralToken | undefined;

          if (firstFilter.subject.type === SparqlTokenType.VAR) {
            firstFilterVar = firstFilter.subject;
            isFirstOperatorGte =
              firstOperatorTokenName ===
              sparqlTokens.sparqlTokenMap.GreaterThanEquals.name;
            isFirstOperatorValid =
              isFirstOperatorGte ||
              firstOperatorTokenName ===
                sparqlTokens.sparqlTokenMap.LessThanEquals.name;
          } else if (firstFilter.value.type === SparqlTokenType.VAR) {
            firstFilterVar = firstFilter.value;
            isFirstOperatorGte =
              firstOperatorTokenName ===
              sparqlTokens.sparqlTokenMap.LessThanEquals.name;
            isFirstOperatorValid =
              isFirstOperatorGte ||
              firstOperatorTokenName ===
                sparqlTokens.sparqlTokenMap.GreaterThanEquals.name;
          }
          if (firstFilter.subject.type === SparqlTokenType.LITERAL) {
            firstFilterLiteral = firstFilter.subject;
          } else if (firstFilter.value.type === SparqlTokenType.LITERAL) {
            firstFilterLiteral = firstFilter.value;
          }

          if (secondFilter.subject.type === SparqlTokenType.VAR) {
            secondFilterVar = secondFilter.subject;
            isSecondOperatorLte =
              secondOperatorTokenName ===
              sparqlTokens.sparqlTokenMap.LessThanEquals.name;
            isSecondOperatorValid =
              isSecondOperatorLte ||
              secondOperatorTokenName ===
                sparqlTokens.sparqlTokenMap.GreaterThanEquals.name;
          } else if (secondFilter.value.type === SparqlTokenType.VAR) {
            secondFilterVar = secondFilter.value;
            isSecondOperatorLte =
              secondOperatorTokenName ===
              sparqlTokens.sparqlTokenMap.GreaterThanEquals.name;
            isSecondOperatorValid =
              isSecondOperatorLte ||
              secondOperatorTokenName ===
                sparqlTokens.sparqlTokenMap.LessThanEquals.name;
          }
          if (secondFilter.subject.type === SparqlTokenType.LITERAL) {
            secondFilterLiteral = secondFilter.subject;
          } else if (secondFilter.value.type === SparqlTokenType.LITERAL) {
            secondFilterLiteral = secondFilter.value;
          }

          if (
            // ignore invalid betweens filters
            !firstFilterVar ||
            !secondFilterVar ||
            !firstFilterLiteral ||
            !secondFilterLiteral ||
            !isFirstOperatorValid ||
            !isSecondOperatorValid ||
            // ignore unbound filter variables
            !objectImages.has(firstFilterVar.var.image) ||
            !objectImages.has(secondFilterVar.var.image) ||
            // ignore comparisons across different variables
            firstFilterVar.var.image !== secondFilterVar.var.image
          ) {
            // ignore
          } else if (
            (isFirstOperatorGte && isSecondOperatorLte) ||
            (!isFirstOperatorGte && !isSecondOperatorLte)
          ) {
            const inferredRange: DatatypeRange =
              range ||
              (firstFilterLiteral.langIri &&
                (getFullIri(
                  firstFilterLiteral.langIri,
                  allNamespaces
                ) as any)) ||
              (secondFilterLiteral.langIri &&
                (getFullIri(
                  secondFilterLiteral.langIri,
                  allNamespaces
                ) as any)) ||
              null;
            if (BETWEEN_RANGES.has(inferredRange)) {
              addAttributeFilter(
                firstFilterVar.var,
                {
                  subject,
                  operator: {
                    iri: propertyIri,
                    operatorType: OperatorType.DATATYPE,
                    range: inferredRange,
                    type: DatatypeOperatorType.BETWEEN,
                  },
                  value: isFirstOperatorGte
                    ? `${formatLiteral(
                        firstFilterLiteral.literal
                      )}${DATE_SPLIT_SYMBOL}${formatLiteral(
                        secondFilterLiteral.literal
                      )}`
                    : `${formatLiteral(
                        secondFilterLiteral.literal
                      )}${DATE_SPLIT_SYMBOL}${formatLiteral(
                        firstFilterLiteral.literal
                      )}`,
                },
                filterResult
              );
              return false;
            }
          }
        }

        // add unprocessed tokens for unprocessed filters
        filterResult.filters.forEach((filter) => {
          addUnprocessedToken(getSparqlTokenImage(filter.subject));
          addUnprocessedToken(getSparqlTokenImage(filter.value));
        });

        return true;
      }
    );

    if (skippedFilters.length || !filterResults) {
      let datatypeOperatorType = DatatypeOperatorType.EXISTS;
      if (options?.isRuleThen) {
        // add the object as an unprocessed token
        addUnprocessedToken(objectToken);
        datatypeOperatorType =
          DEFAULT_OPERATOR_TYPE_BY_DATATYPE_RANGE[range || ''] ||
          DatatypeOperatorType.EQUALS;
      }

      // fallback to an "exists" or "equals" datatype filter for skipped or missing filters
      addAttributeFilter(objectToken, {
        subject,
        operator: {
          iri: propertyIri,
          operatorType: OperatorType.DATATYPE,
          range,
          type: datatypeOperatorType,
        },
        value: '',
      });
    }
  };

  const filters: Filter[] = [];

  tripleResults.forEach((tripleResult) => {
    const subjectToken = getSparqlTokenImage(tripleResult.subject);
    const objectToken = getSparqlTokenImage(tripleResult.object);
    const subject = getOrAssignVariable(subjectToken);

    const maybePropertyIri =
      getTokenName(tripleResult.predicate) ===
      sparqlTokens.sparqlTokenMap.A.name
        ? RDF_TYPE_IRI
        : getFullIri(tripleResult.predicate, allNamespaces);
    if (!maybePropertyIri) {
      console.warn(
        `could not resolve property iri: ${tripleResult.predicate.image}`
      );
    }

    const propertyIri =
      maybePropertyIri === options?.stardogLabelIri
        ? STARDOG_LABEL_IRI
        : maybePropertyIri;

    const property = propertiesByIri[propertyIri];

    if (propertyIri === RDF_TYPE_IRI) {
      if (
        // object is an iri
        tripleResult.object.type === SparqlTokenType.IRI
      ) {
        // add "classify as" type query builder filter
        filters.push({
          subject,
          operator: {
            direction: Direction.OUTBOUND,
            iri: RDF_TYPE_IRI,
            operatorType: OperatorType.OBJECT,
            type: ObjectOperatorType.CONNECTED,
          },
          value: {
            id: uuid(),
            iri: getFullIri(objectToken, allNamespaces),
          },
        });
      }
    } else if (
      // object is an iri
      tripleResult.object.type === SparqlTokenType.IRI ||
      // property is explicitly defined as a relationship
      (property && !property.isDatatype) ||
      // token is otherwise assumed to be an IRI
      canAssumeIriToken(objectToken)
    ) {
      // add relationship query builder filter
      filters.push({
        subject,
        operator: {
          direction: Direction.OUTBOUND,
          iri: propertyIri,
          operatorType: OperatorType.OBJECT,
          type: ObjectOperatorType.CONNECTED,
        },
        value: getOrAssignVariable(objectToken),
      });
    } else {
      const range =
        (property &&
          Array.from(property.ranges).find(
            (rangeIri): rangeIri is NonNullable<DatatypeRange> =>
              XSD_DATATYPE_IRIS.includes(rangeIri as any)
          )) ||
        null;

      const textMatchToken = textMatchByImage[objectToken.image];
      if (textMatchToken) {
        if (textMatchToken.type === SparqlTokenType.LITERAL) {
          // add the text match filter to be processed later
          addAttributeFilter(objectToken, {
            subject,
            operator: {
              iri: propertyIri,
              operatorType: OperatorType.DATATYPE,
              range,
              type: DatatypeOperatorType.EQUALS,
            },
            value: formatLiteral(textMatchToken.literal),
          });
        } else {
          // add unprocessed tokens for unprocessed text match
          addUnprocessedToken(subjectToken);
          addUnprocessedToken(objectToken);
        }
      } else if (tripleResult.object.type === SparqlTokenType.VAR) {
        // only process literal filters once per object variable image
        if (!processedLiteralFilters.has(objectToken.image)) {
          processedLiteralFilters.add(objectToken.image);
          unprocessedLiteralFilters.delete(objectToken.image);
          processLiteralFilters(
            literalFilterResultsByImage[objectToken.image],
            subject,
            objectToken,
            propertyIri,
            range
          );
        }
      } else {
        // fallback to an "equals" datatype for an unprocessable filter
        addAttributeFilter(objectToken, {
          subject,
          operator: {
            iri: propertyIri,
            operatorType: OperatorType.DATATYPE,
            range,
            type:
              DEFAULT_OPERATOR_TYPE_BY_DATATYPE_RANGE[range || ''] ||
              DatatypeOperatorType.EQUALS,
          },
          value: formatLiteral(objectToken),
        });
      }
    }
  });

  Object.values(iriTokens).forEach((token) => {
    filters.push({
      subject: getOrAssignVariable(token),
      operator: {
        iri: INSTANCE_IRI,
        operatorType: OperatorType.INSTANCE,
        type: InstanceOperatorType.IN,
      },
      value: `\`${getFullIri(token, namespaces)}\``,
    });
  });

  comparisonFilterResults.forEach((filterResult) => {
    const [filter, ...rest] = filterResult.filters;

    // only support one filter statement per comparison
    if (!rest.length) {
      // ignore instance inequality filters
      if (isInequalityFilter(filter)) {
        return;
      }

      const subjectToken = getSparqlTokenImage(filter.subject);
      const objectToken = getSparqlTokenImage(filter.value);

      // find the subject datatype filter for the comparison
      const [subjectFilter, ...otherSubjectFilters] =
        attributeFiltersByImage[subjectToken.image] || [];

      // find the object datatype filter for the comparison
      const [objectFilter, ...otherObjectFilters] =
        attributeFiltersByImage[objectToken.image] || [];

      if (
        // comparison must be across different variables
        subjectToken.image !== objectToken.image &&
        // only support one filter statement per variable image
        !otherSubjectFilters.length &&
        // only support one filter statement per variable image
        !otherObjectFilters.length &&
        // comparison filter must be exists
        subjectFilter &&
        subjectFilter.filter.operator.operatorType === OperatorType.DATATYPE &&
        subjectFilter.filter.operator.type === DatatypeOperatorType.EXISTS &&
        // comparison filter must be exists
        objectFilter &&
        objectFilter.filter.operator.operatorType === OperatorType.DATATYPE &&
        objectFilter.filter.operator.type === DatatypeOperatorType.EXISTS
      ) {
        if (
          getTokenName(filter.operator) ===
          sparqlTokens.sparqlTokenMap.CONTAINS.name
        ) {
          // add comparison contains query builder filter
          filters.push({
            subject: subjectFilter.filter.subject,
            operator: {
              comparison: true,
              iri: subjectFilter.filter.operator.iri,
              operatorType: OperatorType.DATATYPE,
              range: subjectFilter.filter.operator.range,
              type: filter.operatorBang
                ? DatatypeOperatorType.DOES_NOT_CONTAIN
                : DatatypeOperatorType.CONTAINS,
            },
            comparisonSubject: objectFilter.filter.subject,
            comparisonOperator: {
              iri: objectFilter.filter.operator.iri,
              operatorType: OperatorType.DATATYPE,
              range: objectFilter.filter.operator.range,
              type: DatatypeOperatorType.EQUALS,
            },
          });

          // remove the attribute filters since they have been included as a comparison
          delete attributeFiltersByImage[subjectToken.image];
          delete attributeFiltersByImage[objectToken.image];
          return;
        }

        const datatypeOperatorType = tokenToDatatypeOperatorType(
          subjectFilter.filter.operator.range ||
            objectFilter.filter.operator.range,
          filter.operator,
          Boolean(filter.operatorBang)
        );

        if (datatypeOperatorType) {
          // add comparison query builder filter
          filters.push({
            subject: subjectFilter.filter.subject,
            operator: {
              comparison: true,
              iri: subjectFilter.filter.operator.iri,
              operatorType: OperatorType.DATATYPE,
              range: subjectFilter.filter.operator.range,
              type: datatypeOperatorType,
            },
            comparisonSubject: objectFilter.filter.subject,
            comparisonOperator: {
              iri: objectFilter.filter.operator.iri,
              operatorType: OperatorType.DATATYPE,
              range: objectFilter.filter.operator.range,
              type: DatatypeOperatorType.EQUALS,
            },
          });

          // remove the attribute filters since they have been included as a comparison
          delete attributeFiltersByImage[subjectToken.image];
          delete attributeFiltersByImage[objectToken.image];
          return;
        }
      }
    }

    // add unprocessed tokens for unprocessed filters
    filterResult.filters.forEach((filter) => {
      addUnprocessedToken(getSparqlTokenImage(filter.subject));
      addUnprocessedToken(getSparqlTokenImage(filter.value));
    });
  });

  // add remaining attribute filters that were not used in a comparsion
  Object.values(attributeFiltersByImage).forEach((attributeFilters) => {
    const [firstFilter, ...otherFilters] = Array.from(attributeFilters);
    filters.push(firstFilter.filter);

    // only support one filter statement per variable image
    otherFilters.forEach((otherFilter) => {
      otherFilter.filterResult?.filters.forEach((filter) => {
        addUnprocessedToken(getSparqlTokenImage(filter.subject));
        addUnprocessedToken(getSparqlTokenImage(filter.value));
      });
    });
  });

  // add unprocessed tokens for unprocessed filters
  unprocessedLiteralFilters.forEach((varImage) => {
    literalFilterResultsByImage[varImage].forEach((filterResult) =>
      filterResult.filters.forEach((filter) => {
        addUnprocessedToken(getSparqlTokenImage(filter.subject));
        addUnprocessedToken(getSparqlTokenImage(filter.value));
      })
    );
  });

  const scoreTokens = results.tokens.all.filter((token) =>
    TERMINAL_TOKEN_NAMES.has(getTokenName(token))
  );

  const unprocessedTokens = Array.from(unprocessedTokensSet).filter((token) =>
    TERMINAL_TOKEN_NAMES.has(getTokenName(token))
  );

  const score =
    (scoreTokens.length - unprocessedTokens.length) / scoreTokens.length;

  const topExpression = removeDraftFields(createNewTopExpression());
  if (filters.length) {
    topExpression.expressions[0].expressions[0].filters = filters;
  }

  return {
    score,
    tokens: {
      all: results.tokens.all,
      ignored: results.tokens.ignored,
      unprocessed: unprocessedTokens,
    },
    topExpression,
    variablesByImage,
  };
};

export const sparql = (
  query: string,
  { propertiesByIri }: QueryBuilderModel,
  namespaces: Record<string, string>
): {
  score: number;
  tokens: { all: IToken[]; ignored: IToken[]; unprocessed: IToken[] };
  topExpression: TopExpression;
} | null => {
  const results = sparqlTriples(query, namespaces);
  if (!results) {
    return null;
  }

  return sparqlResults(results, { propertiesByIri }, namespaces);
};
